import React from 'react'
import PropTypes from 'prop-types'

export function withFocusWithin(Component) {
    const WrappedComponent = ({ onFocus, onBlur, ...props }) => (
        <FocusWithin onFocus={onFocus} onBlur={onBlur}>
            {({ getFocusProps, isFocused }) =>
                typeof Component === 'string' ? (
                    <Component {...getFocusProps(props)} />
                ) : (
                    <Component {...getFocusProps(props)} isFocused={isFocused} />
                )
            }
        </FocusWithin>
    )
    WrappedComponent.displayName = `WithFocusWithin(${Component.displayName || Component.name || 'Component'})`
    return WrappedComponent
}

export class FocusWithin extends React.Component {
    static propTypes = {
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
    }

    static defaultProps = {
        onBlur: () => {},
        onFocus: () => {},
    }

    static wrapComponent = withFocusWithin

    state = {
        isBlurring: false,
        isFocused: false,
    }

    // Dröj för att synkronisera in tiden mellan FocusWithin händelser och native händelser
    delay = (cb, ...args) =>
        setTimeout(() => {
            if (this.isUnmounted) {
                return
            }
            cb(...args)
        })

    setFocusState = (isFocused, isFocusWithinEvent) => {
        const setStateCb = () => {
            if (this.isUnmounted) return
            this.setState(
                state =>
                    state.isFocused === isFocused
                        ? state
                        : !isFocused
                            ? {
                                  isFocused,
                                  isBlurring: true,
                              }
                            : {
                                  isFocused,
                              }
            )
        }
        // Vänta med hantera native händelser, så att den är synkroniserad med FocusWithin händelser
        if (isFocusWithinEvent) {
            setStateCb()
        } else {
            this.delay(setStateCb)
        }
    }

    // Följ mushändelserna inom FocusWithin "containern". Detta tillåter dig att klicka inuti den närsomhelst.
    // När en "mousedown" händelse inträffar så sparar vi det aktuella "container"-elementet, och när 
    // en "mouseup" händelse inträffar och då vi är utanför "containern", så triggar vi en "blur" händelse.
    // "mouseups"-händelsen kan fortfarande inträffa och hanteras även utanför "containern" medans 
    // aktuell DOM fokus behålls inom container:n, så vi inte triggar några "blur" händelser för element
    // utanför FocusWithin
    onDocumentMouseUp = event => {
        const container = this.mouseDownWithinTarget
        const mouseUpWithinTarget = container && container.contains(event.target)
        const activeElementWithinTarget = container && container.contains(document.activeElement)
        if (!activeElementWithinTarget && !mouseUpWithinTarget) {
            this.setFocusState(false, this.lastBlurEvent ? this.lastBlurEvent.__isFocusWithinEvent : true)
        }
        this.mouseDownWithinTarget = null
        this.lastBlurEvent = null
    }

    getFocusProps = ({ onFocus, onBlur, onMouseDown, ...props } = {}) => {
        return {
            ...props,
            onFocus: event => {
                const propagationStopped = onFocus && onFocus(event) === false
                if (propagationStopped || (event && event.focusWithinDefaultPrevented)) {
                    return
                }
                this.setFocusState(true, event && event.__isFocusWithinEvent)
            },
            onBlur: event => {
                const propagationStopped = onBlur && onBlur(event) === false
                if (propagationStopped || (event && event.focusWithinDefaultPrevented)) {
                    return
                }
                // Om "blur" händelsen inträffar direkt efter en "mousedown" händelse på ett element inom FocusWithin containern,
                // så triggar vi inte "blur"-händelsen direkt, utan först efter att "mouseup"-händelsen inträffat
                if (this.mouseDownWithinTarget) {
                    event.persist && event.persist()
                    this.lastBlurEvent = event
                    return
                }
                this.setFocusState(false, event && event.__isFocusWithinEvent)
            },
            onMouseDown: event => {
                const propagationStopped = onMouseDown && onMouseDown(event) === false
                if (propagationStopped || (event && event.focusWithinDefaultPrevented)) {
                    return
                }
                this.mouseDownWithinTarget = event.currentTarget
            },
        }
    }

    componentDidMount() {
        document.addEventListener('mouseup', this.onDocumentMouseUp)
    }

    componentDidUpdate(_, prevState) {
        if (prevState.isFocused && !this.state.isFocused) {
            this.delay(() => {
                if (this.isUnmounted) {
                    return
                }
                this.setState({
                    isBlurring: false,
                })
                // Kontrollera om fokus-hanteringen faktiskt redan är "blured" i situationer då "click"-händelser på document (DOM) orsakar flera "focus" händelser i följd
                if (!this.state.isFocused) {
                    this.props.onBlur({ __isFocusWithinEvent: true })
                }
            })
        }
        // Kontrollera om fokus-hanteringen fokuserade utanför FocusWithIn containern och inte från ett annan barn-element i containern.
        // Dröj då med "onFocus" så att denna synktroniseras med övriga "blur"-händelser
        if (!this.state.isBlurring && !prevState.isFocused && this.state.isFocused) {
            this.delay(this.props.onFocus, { __isFocusWithinEvent: true })
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true
        document.removeEventListener('mouseup', this.onDocumentMouseUp)
    }

    render() {
        return this.props.children
            ? this.props.children({
                  focusProps: this.getFocusProps(),
                  getFocusProps: this.getFocusProps,
                  // Se till så att isFocused bara ändras av sina egna triggade händelser
                  isFocused: this.state.isFocused || this.state.isBlurring,
              })
            : null
    }
}

export default FocusWithin


// WEBPACK FOOTER //
// ./src/components/Accessibility/FocusWithin.js